import { Heading } from '@postscript/components';
import styled from 'styled-components';
import { MediaQueries } from './MainLayout';

export const StyledBlockHeading = styled.div`
  padding-bottom: var(--spacing-4);
  position: relative;

  > * {
    max-width: 900px;
  }

  h1,
  h2,
  h3 {
    margin-bottom: var(--spacing-2);
    &:last-child {
      margin-bottom: var(--spacing-4);
    }
  }
  p {
    margin-bottom: var(--spacing-4);
  }
`;

export const StyledCardGrid = styled.div<{ desktopColumns?: number }>`
  --cardGridGap: var(--spacing-4);

  ${MediaQueries.desktopOnly} {
    display: grid;
    grid-template-columns: repeat(
      ${({ desktopColumns }) => desktopColumns || '4'},
      1fr
    );
    grid-column-gap: var(--cardGridGap);
    grid-row-gap: var(--cardGridGap);

    > .span-2 {
      grid-column: auto / span 2;
    }
  }

  ${MediaQueries.tabletOnly} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: var(--cardGridGap);
    grid-row-gap: var(--cardGridGap);
  }

  ${MediaQueries.phoneOnly} {
    display: flex;
    flex-direction: column;

    > *:not(:first-child) {
      margin-top: var(--cardGridGap);
    }
  }
`;

export const StyledComponentCardStack = styled.div`
  --cardStackGap: var(--spacing-4);
  margin: 0 auto;

  ${MediaQueries.desktopOnly} {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-column-gap: var(--cardStackGap);
    grid-row-gap: var(--cardStackGap);

    .span-2 {
      grid-column: auto / span 2;
    }
  }

  ${MediaQueries.tabletOnly} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: var(--cardStackGap);
    grid-row-gap: var(--cardStackGap);
  }

  ${MediaQueries.phoneOnly} {
    display: flex;
    flex-direction: column;

    > *:not(:first-child) {
      margin-top: var(--cardStackGap);
    }
  }
`;

export const StyledExampleText = styled.span`
  display: block;
  margin-top: var(--spacing-1);
  font: var(--body-text-x-small);
`;

export const StyledHeroBlock = styled.section`
  background: var(--main-bkg-color);
  display: flex;
  align-items: center;

  ${MediaQueries.desktopAndTablet} {
    min-height: 100vh;
    padding: 216px 0;
  }

  > div {
    margin: 0 auto;
    max-width: 1440px;
  }

  ${MediaQueries.desktopOnly} {
    > div {
      padding: 0 96px;
      display: grid;
      grid-template-columns: repeat(2, 50%);
      grid-column-gap: var(--spacing-4);
      > div > * {
        width: 100%;
      }
    }

    img {
      margin-top: -12%;
    }
  }

  ${MediaQueries.tabletOnly} {
    > div {
      padding: 0 48px;
    }

    img {
      display: none;
    }
  }

  ${MediaQueries.phoneOnly} {
    min-height: 80vh;
    padding: 60px 0;

    > div {
      display: flex;
      flex-direction: column-reverse;
      padding: 0 24px;
    }

    img {
      max-width: 280px;
      margin: 0 auto var(--spacing-6);
    }
  }
`;

export const StyledHeroHeading = styled(Heading)`
  margin-bottom: var(--spacing-1);
  text-shadow: var(--main-bkg-color) 1px 0 12px;

  @media only screen and (min-width: 1281px) {
    font: 700 7.5rem/6.25rem var(--heading-typeface);
  }

  @media only screen and (min-width: 768px) and (max-width: 1280px) {
    font: 700 9vw/7.56vw var(--heading-typeface);

    & + p {
      font: 2vw/2.72vw var(--body-text-typeface);
      text-shadow: var(--main-bkg-color) 1px 0 6px;
    }
  }

  ${MediaQueries.phoneOnly} {
    font: 700 18vw/15.12vw var(--heading-typeface);

    & + p {
      text-shadow: var(--main-bkg-color) 1px 0 6px;
    }
  }
`;

export const StyledInputLayout = styled.figure`
  display: flex;
  width: 100%;
  align-items: center;
  gap: var(--spacing-1);

  div {
    width: 100%;
  }
`;

export const StyledPullQuote = styled.figure`
  background: url('img/quote.svg') var(--spacing-4) var(--spacing-4) / 15%
    no-repeat var(--mint-core);
  border-radius: var(--border-radius-large);
  box-shadow: var(--box-shadow-medium);
  color: var(--black);
  align-self: flex-start;
  padding: 36px 48px;

  blockquote {
    font: var(--heading-x-small);
    margin-bottom: var(--spacing-2);
  }

  figcaption {
    text-align: right;
    font: var(--heading-xx-small);
    span,
    cite {
      display: block;
    }
  }
`;

export const StyledTabPanelIntro = styled.div`
  padding: var(--spacing-2) 0 var(--spacing-6);
  min-height: 96px;
  max-width: 660px;

  p {
    margin-bottom: 0;
  }
`;

export const StyledTabPanelText = styled.article`
  h3 {
    margin-bottom: 0;
  }
  p {
    margin-bottom: var(--spacing-3);
  }
`;

export const TokenExampleCard = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font: var(--body-text-small);
  height: 60px;
  width: 60px;
  border: 1px solid var(--border-color-dim);
`;

export const TwoColumnLayout = styled.div<{ margin?: string }>`
  margin: ${({ margin }) => margin || '0'};

  > div {
    justify-content: flex-start;
  }

  ${MediaQueries.desktopOnly} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: var(--systemBlockGap);
    > div > * {
      width: 100%;
    }
  }

  ${MediaQueries.tabletOnly} {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-column-gap: var(--systemBlockGap);

    > *:first-child {
      grid-column: auto / span 2;
    }
    > *:nth-child(2) {
      grid-column: auto / span 1;
    }
  }

  ${MediaQueries.phoneOnly} {
    display: flex;
    flex-direction: column;
    > *:last-child {
      margin-top: var(--systemBlockGap);
    }
  }
`;
